import {
	FormProvider,
	getFormProps,
	getInputProps,
	useForm,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { Form, useActionData } from '@remix-run/react'
import { z } from 'zod'
import { FileInput } from '#app/components/file-input'
import { ErrorList, Field } from '#app/components/forms'
import { StatusButton } from '#app/components/ui/status-button'
import { useIsPending } from '#app/utils/misc'
import type { action } from './__pmhc-suggestion-editor.server'

export const MAX_UPLOAD_SIZE = 1024 * 1024 * 0.5 // 500kB

export const PmhcEditorSchema = z.object({
	id: z.string().optional(),
	epitope_id_by_iedb: z.string().optional(),
	epitope_position: z.string().optional(),
	immunological_background_id: z.string().optional(),
	intent: z.literal('submit'),
	link_epitope_id_by_iedb: z.string().optional(),
	link_to_reference: z.string().url(),
	link_to_source_protein: z.string().url(),
	link_to_structure_type: z.string().url().optional(),
	mhc_allele: z.string(),
	reference: z.string().optional(),
	sequence: z.string().min(8).max(12),
	source_organism: z.string(),
	source_protein: z.string(),
	structure_type: z.string(),
	image_file: z
		.instanceof(File)
		.refine(
			file => file.type.startsWith('image/'),
			'Only image files are allowed',
		)
		.refine(
			file => file.size <= MAX_UPLOAD_SIZE,
			`File size must be less than ${MAX_UPLOAD_SIZE / (1024 * 1024)}MB`,
		),
	pdb_file: z
		.instanceof(File)
		.optional()
		.refine(file => !file || file.size <= MAX_UPLOAD_SIZE),
})

export function PmhcEditor({ pmhc }: { pmhc?: any } = {}) {
	const isPending = useIsPending()
	const actionData = useActionData<typeof action>()
	const [form, fields] = useForm({
		id: 'pmhc-editor',
		constraint: getZodConstraint(PmhcEditorSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: PmhcEditorSchema })
		},
		defaultValue: {
			id: pmhc?.id || '',
			sequence: pmhc?.sequence || 'ABCDEFGH',
			mhc_allele: pmhc?.mhc_allele_id || '',
			structure_type: pmhc?.structure_type_id || '',
			source_organism: pmhc?.source_organism || '',
			source_protein: pmhc?.source_protein || '',
			link_to_reference: pmhc?.link_para_reference || 'https://google.com',
			link_to_source_protein:
				pmhc?.link_para_source_protein || 'https://google.com',
			link_to_structure_type:
				pmhc?.link_para_structure_type || 'https://google.com',
			epitope_position: pmhc?.epitope_position || '',
			reference: pmhc?.reference || '',
			epitope_id_by_iedb: pmhc?.epitope_id_by_iedb || '',
			link_epitope_id_by_iedb:
				pmhc?.link_epitope_id_by_iedb || 'https://google.com',
			immunological_background_id: pmhc?.immunological_background_id || '',
		},
		shouldRevalidate: 'onBlur',
	})

	const handleFileChange = (file: File | null) => {
		if (file) {
			const reader = new FileReader()
			reader.onload = e => {
				console.log(e.target?.result)
			}
			reader.readAsDataURL(file)
		}
	}

	return (
		<FormProvider context={form.context}>
			<Form method="POST" encType="multipart/form-data" {...getFormProps(form)}>
				<div className="space-y-8">
					{/* Basic Information */}
					<div className="bg-white rounded-lg border p-6 shadow-sm">
						<h2 className="text-lg font-semibold mb-4 text-gray-900">
							Basic Information
						</h2>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<input {...getInputProps(fields.id, { type: 'hidden' })} />
							<Field
								labelProps={{
									htmlFor: fields.sequence.id,
									children: 'Peptide Sequence',
								}}
								inputProps={{
									...getInputProps(fields.sequence, { type: 'text' }),
									autoFocus: true,
									autoComplete: 'sequence',
									placeholder: 'e.g., ABCDEFGH',
								}}
								errors={fields.sequence.errors}
							/>
							<Field
								labelProps={{
									htmlFor: fields.mhc_allele.id,
									children: 'MHC Allele',
								}}
								inputProps={{
									...getInputProps(fields.mhc_allele, { type: 'text' }),
									autoComplete: 'mhc_allele',
									placeholder: 'e.g., HLA-A*02:01',
								}}
								errors={fields.mhc_allele.errors}
							/>
							<Field
								labelProps={{
									htmlFor: fields.structure_type.id,
									children: 'Structure Type',
								}}
								inputProps={{
									...getInputProps(fields.structure_type, { type: 'text' }),
									autoComplete: 'structure_type',
									placeholder: 'e.g., Crystal Structure',
								}}
								errors={fields.structure_type.errors}
							/>
							<Field
								labelProps={{
									htmlFor: fields.source_organism.id,
									children: 'Source Organism',
								}}
								inputProps={{
									...getInputProps(fields.source_organism, { type: 'text' }),
									autoComplete: 'source_organism',
									placeholder: 'e.g., Homo sapiens',
								}}
								errors={fields.source_organism.errors}
							/>
							<div className="md:col-span-2">
								<Field
									labelProps={{
										htmlFor: fields.source_protein.id,
										children: 'Source Protein',
									}}
									inputProps={{
										...getInputProps(fields.source_protein, { type: 'text' }),
										autoComplete: 'source_protein',
										placeholder: 'e.g., Influenza A virus protein',
									}}
									errors={fields.source_protein.errors}
								/>
							</div>
						</div>
					</div>

					{/* File Uploads */}
					<div className="bg-white rounded-lg border p-6 shadow-sm">
						<h2 className="text-lg font-semibold mb-4 text-gray-900">
							File Uploads
						</h2>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-3">
									PMHC Structure Image <span className="text-red-500">*</span>
								</label>
								<FileInput
									initialFile={undefined}
									onFileChange={handleFileChange}
									acceptedFileTypes="image/*"
									description="Upload an image showing the PMHC structure (JPG, PNG, etc.). Max size: 500KB"
									inputProps={{
										...getInputProps(fields.image_file, { type: 'file' }),
										autoComplete: 'image_file',
									}}
									errors={fields.image_file.errors}
								/>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-3">
									PDB Structure File
								</label>
								<FileInput
									initialFile={undefined}
									onFileChange={handleFileChange}
									acceptedFileTypes="chemical/x-pdb"
									description="Optional: Upload the 3D structure file in PDB format. Max size: 500KB"
									inputProps={{
										...getInputProps(fields.pdb_file, { type: 'file' }),
										autoComplete: 'pdb_file',
									}}
									errors={fields.pdb_file.errors}
								/>
							</div>
						</div>
					</div>

					{/* Reference Links */}
					<div className="bg-white rounded-lg border p-6 shadow-sm">
						<h2 className="text-lg font-semibold mb-4 text-gray-900">
							Reference Links
						</h2>
						<div className="space-y-3">
							<Field
								labelProps={{
									htmlFor: fields.link_to_reference.id,
									children: 'Publication Reference URL',
								}}
								inputProps={{
									...getInputProps(fields.link_to_reference, { type: 'url' }),
									autoComplete: 'link_to_reference',
									placeholder: 'https://pubmed.ncbi.nlm.nih.gov/...',
								}}
								errors={fields.link_to_reference.errors}
							/>
							<Field
								labelProps={{
									htmlFor: fields.link_to_source_protein.id,
									children: 'Source Protein Reference URL',
								}}
								inputProps={{
									...getInputProps(fields.link_to_source_protein, {
										type: 'url',
									}),
									autoComplete: 'link_to_source_protein',
									placeholder: 'https://www.uniprot.org/...',
								}}
								errors={fields.link_to_source_protein.errors}
							/>
							<Field
								labelProps={{
									htmlFor: fields.link_to_structure_type.id,
									children: 'Structure Type Reference URL',
								}}
								inputProps={{
									...getInputProps(fields.link_to_structure_type, {
										type: 'url',
									}),
									autoComplete: 'link_to_structure_type',
									placeholder: 'https://www.rcsb.org/...',
								}}
								errors={fields.link_to_structure_type.errors}
							/>
						</div>
					</div>

					{/* Additional Information */}
					<div className="bg-gray-50 rounded-lg border p-6">
						<h2 className="text-lg font-semibold mb-2 text-gray-900">
							Additional Information
						</h2>
						<p className="text-sm text-gray-600 mb-4">
							These fields are optional but help provide more context for your
							submission.
						</p>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<Field
								labelProps={{
									htmlFor: fields.epitope_position.id,
									children: 'Epitope Position',
								}}
								inputProps={{
									...getInputProps(fields.epitope_position, { type: 'text' }),
									autoComplete: 'epitope_position',
									placeholder: 'e.g., 1-10',
								}}
								errors={fields.epitope_position.errors}
							/>
							<Field
								labelProps={{
									htmlFor: fields.immunological_background_id.id,
									children: 'Immunological Background',
								}}
								inputProps={{
									...getInputProps(fields.immunological_background_id, {
										type: 'text',
									}),
									autoComplete: 'immunological_background_id',
									placeholder: 'e.g., Viral infection',
								}}
								errors={fields.immunological_background_id.errors}
							/>
							<Field
								labelProps={{
									htmlFor: fields.reference.id,
									children: 'Additional Reference',
								}}
								inputProps={{
									...getInputProps(fields.reference, { type: 'text' }),
									autoComplete: 'reference',
									placeholder: 'e.g., DOI or citation',
								}}
								errors={fields.reference.errors}
							/>
							<Field
								labelProps={{
									htmlFor: fields.link_epitope_id_by_iedb.id,
									children: 'IEDB Epitope ID Link',
								}}
								inputProps={{
									...getInputProps(fields.link_epitope_id_by_iedb, {
										type: 'url',
									}),
									autoComplete: 'link_epitope_id_by_iedb',
									placeholder: 'https://www.iedb.org/...',
								}}
								errors={fields.link_epitope_id_by_iedb.errors}
							/>
						</div>
					</div>
				</div>

				<ErrorList errors={form.errors} id={form.errorId} />

				<div className="mt-8 flex justify-center">
					<StatusButton
						type="submit"
						size="lg"
						name="intent"
						value="submit"
						status={isPending ? 'pending' : (form.status ?? 'idle')}
						className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-3"
					>
						Submit Suggestion
					</StatusButton>
				</div>
			</Form>
		</FormProvider>
	)
}

export default function SuggestRoute() {
	return (
		<main className="container mx-auto px-4 py-8 max-w-4xl">
			<div className="mb-8 text-center">
				<h1 className="text-3xl font-bold text-gray-900 mb-2">
					Submit a New PMHC
				</h1>
				<p className="text-gray-600 max-w-2xl mx-auto">
					Contribute to the Crosstope database by submitting a new peptide-MHC
					complex. Your submission will be reviewed by our team before being
					added to the public database.
				</p>
			</div>
			<PmhcEditor />
		</main>
	)
}
